<template>
  <div class="am-vehicle-detail-information">
    <div class="am-vehicle-detail-information__header">
      <div>{{ vehicleTitle }}</div>
      <button aria-label="Overlay schließen" @click="$emit('close')">
        <amui-icon name="close" class="am-icon-size-md" />
      </button>
    </div>
    <div class="am-vehicle-detail-information__content">
      <div>
        <amui-headline html-tag="h3" looks-like="h6"
          >{{ $t('vdp.detailInformation.consumptionProperties.headline')
          }}<sup>1</sup></amui-headline
        >
        <div class="am-vehicle-detail-information__consumption">
          <div>
            <div class="am-vehicle-detail-information__properties">
              <div
                v-for="(property, index) in consumptionProperties"
                :key="property.label + index"
              >
                <div>{{ property.label }}</div>
                <div>{{ property.value }}</div>
              </div>
            </div>
          </div>
          <div v-if="showEnergyEfficiencyTable">
            <amui-card bordered inline-block>
              <amui-card-section>
                <amui-efficiency-class-bars
                  :efficiency-class="energyEfficiencyClass"
                />
              </amui-card-section>
            </amui-card>
          </div>
        </div>
      </div>
      <div class="am-vehicle-detail-information__technical">
        <amui-headline html-tag="h3" looks-like="h6">{{
          $t('vdp.detailInformation.technicalProperties.headline')
        }}</amui-headline>
        <amui-content-collapsable
          :label-collapsed="
            $t('vdp.detailInformation.collapsable.label.collapsed')
          "
        >
          <div
            class="am-vehicle-detail-information__technical-properties-wrapper"
          >
            <div class="am-vehicle-detail-information__properties">
              <div
                v-for="(property, index) in technicalProperties"
                :key="property.label + index"
              >
                <div>{{ property.label }}</div>
                <div>{{ property.value }}</div>
              </div>
            </div>
          </div>
        </amui-content-collapsable>
      </div>
      <div
        v-if="historyProperties.length"
        class="am-vehicle-detail-information__history"
      >
        <amui-headline html-tag="h3" looks-like="h6">{{
          $t('vdp.detailInformation.historyProperties.headline')
        }}</amui-headline>
        <amui-content-collapsable
          :label-collapsed="
            $t('vdp.detailInformation.collapsable.label.collapsed')
          "
        >
          <div
            class="am-vehicle-detail-information__history-properties-wrapper"
          >
            <div class="am-vehicle-detail-information__properties">
              <div
                v-for="(property, index) in historyProperties"
                :key="property.label + index"
              >
                <div>{{ property.label }}</div>
                <div>{{ property.value }}</div>
              </div>
            </div>
          </div>
        </amui-content-collapsable>
      </div>
      <div class="am-vehicle-detail-information__equipment">
        <amui-headline html-tag="h3" looks-like="h6">{{
          $t('vdp.detailInformation.equipmentFeatures.headline')
        }}</amui-headline>
        <amui-content-collapsable
          :label-collapsed="
            $t('vdp.detailInformation.collapsable.label.collapsed')
          "
        >
          <div class="am-vehicle-detail-information__equipment-items">
            <div
              v-for="c in categorizedFeatures"
              :key="c.headline"
              class="am-vehicle-detail-information__equipment-item"
            >
              <div>{{ c.headline }}</div>
              <div>
                <div v-for="feature in c.list" :key="feature">
                  {{ feature }}
                </div>
              </div>
            </div>
          </div>
        </amui-content-collapsable>
      </div>
      <div class="am-vehicle-detail-information__description">
        <amui-headline html-tag="h3" looks-like="h6">{{
          $t('vdp.detailInformation.description.headline')
        }}</amui-headline>
        <amui-content-collapsable
          :label-collapsed="
            $t('vdp.detailInformation.collapsable.label.collapsed')
          "
        >
          <div
            v-html="description"
            class="am-vehicle-detail-information__description-items"
          ></div>
        </amui-content-collapsable>
      </div>

      <div>
        <div class="am-vehicle-detail-information__legal">
          1.
          <amui-i18n :term="$t('legal.consumptionAndEmission')" tag="span">
            <a slot="link" href="https://www.dat.de/co2/" target="_blank">
              https://www.dat.de/co2/
            </a>
          </amui-i18n>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AmuiIcon } from '@/../ui/components/icon'
import { AmuiI18n } from '@/../ui/components/i18n'
import { AmuiContentCollapsable } from '@/../ui/components/content-collapsable'
import { AmuiHeadline } from '@/../ui/components/headline'
import { AmuiCard, AmuiCardSection } from '@/../ui/components/card'
import { AmuiEfficiencyClassBars } from '@/../ui/components/efficiency-class-bars'

import { categorizedFeaturesPropertyList } from '@/shared/definitions/vehicle-features.js'
import { consumptionPropertyList } from '@/shared/definitions/vehicle-consumption.js'
import { technicalPropertyList } from '@/shared/definitions/vehicle-technical.js'
import { historyPropertyList } from '@/shared/definitions/vehicle-history.js'

import { findDeepPropertyValue } from '@/../utils/object.js'
import { formatCount } from '@/../utils/formatter.js'
import { getTitle as getVehicleTitle } from '@/../utils/vehicle'

export default {
  name: 'AmVehicleDetailInformation',

  components: {
    AmuiIcon,
    AmuiI18n,
    AmuiContentCollapsable,
    AmuiHeadline,
    AmuiCard,
    AmuiCardSection,
    AmuiEfficiencyClassBars
  },

  props: {
    rawVehicleData: {
      type: Object,
      required: true
    }
  },

  computed: {
    vehicleTitle() {
      let title = null

      if (this.rawVehicleData) {
        title = getVehicleTitle(this.rawVehicleData)
      }

      return title
    },
    showEnergyEfficiencyTable() {
      return this.rawVehicleData.amNedcValues !== undefined
    },
    energyEfficiencyClass() {
      return this.rawVehicleData.energyEfficiencyClass
    },
    consumptionProperties() {
      return this.getTranslatedPropertyList(consumptionPropertyList)
    },
    technicalProperties() {
      return this.getTranslatedPropertyList(technicalPropertyList)
    },
    historyProperties() {
      return this.getTranslatedPropertyList(historyPropertyList)
    },
    categorizedFeatures() {
      return Object.keys(categorizedFeaturesPropertyList)
        .map(categoryId => ({
          headline: this.$t('vdp.featureCategory.' + categoryId),
          list: this.getTranslatedFeatureList(
            categorizedFeaturesPropertyList[categoryId],
            this.rawVehicleData
          )
        }))
        .filter(obj => obj.list.length > 0)
    },
    description() {
      const { description } = this.rawVehicleData
      const isCreole = String(description || '').indexOf('\\') !== -1
      let html = description || ''
      html = html.replace(/(\*\s.*?)\\{4}/gm, '<ul>$1</ul>')
      html = html.replace(/\*\s(.*?)\\{2}/g, '<li>$1</li>')
      //html = html.replace(/(<li>.*?<br>)/g, '<ul>$1</ul>')
      html = html.replace(/\\+/g, '<br>')
      html = html.replace(/----/g, '<br>')
      html = html.replace(/\*\*(.*?)\*\*/gi, '<h4>$1</h4>')
      if (!isCreole) html = html.replace(/,\s/g, ',<br>')

      return html
    }
  },
  methods: {
    getTranslatedFeatureList(properties, vehicleObject) {
      const list = []
      properties.forEach(property => {
        if (
          typeof property === 'object' &&
          'key' in property &&
          'value' in property
        ) {
          // used for single- or multiselect properties to display only a certain value
          if (
            (Array.isArray(vehicleObject[property.key]) &&
              vehicleObject[property.key].includes(property.value)) ||
            (typeof vehicleObject[property.key] === 'string' &&
              vehicleObject[property.key] === property.value)
          ) {
            list.push(this.$t('vehicle.' + property.key + '' + property.value))
          }
        } else if (Array.isArray(vehicleObject[property])) {
          // used for multiselect fields to display all available array entries as features at once
          vehicleObject[property].forEach(propertyValue => {
            list.push(this.$t('vehicle.' + property + '' + propertyValue))
          })
        } else if (typeof vehicleObject[property] === 'string') {
          // used for singleselect fields to display the selected value
          list.push(
            this.$t('vehicle.' + property + '' + vehicleObject[property])
          )
        } else if (vehicleObject[property] !== undefined) {
          // used for all other cases, mostly that would be truthy boolean properties
          list.push(this.$t('vehicle.' + property))
        }
      })
      return list
    },
    getTranslatedPropertyList(properties) {
      const defaultPropertyObject = {
        key: null,
        value: null
      }
      const data = []

      const {
        vehicleClass,
        hybridPlugin,
        fuel,
        consumptionUnit
      } = this.rawVehicleData

      const isPluginHybrid = hybridPlugin === true
      const isElectric = fuel === 'ELECTRICITY'

      const addConsumptionFuelEntry = (key, value) => {
        data.push({
          label: this.getPropertyLabelTranslation(key),
          value: this.getPropertyValueTranslation(key, {
            consumption: String(value).replace('.', ','),
            unit: this.getPropertyValueTranslation(
              'consumptionUnit',
              consumptionUnit
            )
          })
        })
      }

      const addConsumptionPowerEntry = (key, value) => {
        data.push({
          label: this.getPropertyLabelTranslation(key),
          value: this.getPropertyValueTranslation(key, {
            consumption: value // unit is static: kWh/100km
          })
        })
      }

      const addEmissionEntry = (key, value) => {
        data.push({
          label: this.getPropertyLabelTranslation(key),
          value: this.getPropertyValueTranslation(key, {
            emission: String(value).replace('.', ',')
          })
        })
      }

      properties.forEach(property => {
        //normalize property input to an object structure
        property = Object.assign(
          {},
          defaultPropertyObject,
          typeof property === 'string' ? { key: property } : property
        )

        let propertyValue = findDeepPropertyValue(
          this.rawVehicleData,
          property.key
        )

        if (propertyValue === undefined) {
          // NEFZ values should be always displayed, even without measurement values
          if (isElectric) {
            if (
              [
                'amNedcValues.consumptionPowerCombined',
                'amNedcValues.co2EmissionCombined'
              ].includes(property.key)
            ) {
              propertyValue = '-'
            }
          } else if (isPluginHybrid) {
            if (
              [
                'amNedcValues.consumptionFuelCombinedWeighted',
                'amNedcValues.consumptionPowerCombinedWeighted',
                'amNedcValues.co2EmissionCombinedWeighted'
              ].includes(property.key)
            ) {
              propertyValue = '-'
            }
          } else {
            // all nonelectric vehicles
            if (
              [
                'amNedcValues.consumptionFuelCombined',
                'amNedcValues.co2EmissionCombined'
              ].includes(property.key)
            ) {
              propertyValue = '-'
            }
          }
        }

        if (propertyValue !== undefined) {
          if (
            [
              'amNedcValues.consumptionFuelCombined',
              'amNedcValues.consumptionFuelCity',
              'amNedcValues.consumptionFuelOverland',
              'amNedcValues.consumptionFuelCombinedWeighted',
              'amNedcValues.consumptionPowerCombined',
              'amNedcValues.consumptionPowerCombinedWeighted',
              'amNedcValues.co2EmissionCombined',
              'amNedcValues.co2EmissionCombinedWeighted',
              'wltpValues.consumptionFuelCombined',
              'wltpValues.consumptionFuelCombinedWeighted',
              'wltpValues.consumptionPowerCombined',
              'wltpValues.consumptionPowerCombinedWeighted',
              'wltpValues.co2EmissionCombined',
              'wltpValues.co2EmissionCombinedWeighted'
            ].includes(property.key)
          ) {
            if (isElectric) {
              if (
                [
                  'amNedcValues.consumptionPowerCombined',
                  'wltpValues.consumptionPowerCombined'
                ].includes(property.key)
              ) {
                addConsumptionPowerEntry(property.key, propertyValue)
              } else if (
                [
                  'amNedcValues.co2EmissionCombined',
                  'wltpValues.co2EmissionCombined'
                ].includes(property.key)
              ) {
                addEmissionEntry(property.key, propertyValue)
              }
            } else if (isPluginHybrid) {
              if (
                [
                  'amNedcValues.consumptionFuelCombinedWeighted',
                  'wltpValues.consumptionFuelCombinedWeighted'
                ].includes(property.key)
              ) {
                addConsumptionFuelEntry(property.key, propertyValue)
              } else if (
                [
                  'amNedcValues.consumptionPowerCombinedWeighted',
                  'wltpValues.consumptionPowerCombinedWeighted'
                ].includes(property.key)
              ) {
                addConsumptionPowerEntry(property.key, propertyValue)
              } else if (
                [
                  'amNedcValues.co2EmissionCombinedWeighted',
                  'wltpValues.co2EmissionCombinedWeighted'
                ].includes(property.key)
              ) {
                addEmissionEntry(property.key, propertyValue)
              }
            } else {
              // all nonelectric vehicles
              if (
                [
                  'amNedcValues.consumptionFuelCombined',
                  'amNedcValues.consumptionFuelCity',
                  'amNedcValues.consumptionFuelOverland',
                  'wltpValues.consumptionFuelCombined'
                ].includes(property.key)
              ) {
                addConsumptionFuelEntry(property.key, propertyValue)
              } else if (
                [
                  'amNedcValues.co2EmissionCombined',
                  'wltpValues.co2EmissionCombined'
                ].includes(property.key)
              ) {
                addEmissionEntry(property.key, propertyValue)
              }
            }
          } else if (property.key === 'wltpValues.electricRange') {
            data.push({
              label: this.getPropertyLabelTranslation(
                property.key + (isPluginHybrid ? 'PluginHybrid' : '')
              ),
              value: this.getPropertyValueTranslation(property.key, {
                range: propertyValue
              })
            })
          } else if (property.key === 'cubicCapacity') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                cubicCapacity: propertyValue
              })
            })
          } else if (property.key === 'power') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                kw: parseInt(propertyValue),
                ps: Math.round(parseInt(propertyValue) * 1.35962)
              })
            })
          } else if (property.key === 'dimension.width') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'dimension.length') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'dimension.height') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'amPayload') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'amWheelbase') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'amCurbWeight') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'amLadenWeight') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                value: formatCount(propertyValue)
              })
            })
          } else if (property.key === 'energyEfficiencyClass') {
            data.push({
              label: this.getPropertyLabelTranslation(property.key),
              value: this.getPropertyValueTranslation(property.key, {
                energyEfficiencyClass: propertyValue
              })
            })
          } else if (
            ['accidentDamaged', 'damageUnrepaired'].includes(property.key)
          ) {
            if (propertyValue) {
              data.push({
                label: this.getPropertyLabelTranslation(property.key),
                value: this.getPropertyValueTranslation(
                  property.key,
                  propertyValue ? 'YES' : 'NO'
                )
              })
            }
          } else if (property.key === 'generalInspection') {
            if (propertyValue) {
              data.push({
                label: this.getPropertyLabelTranslation(property.key),
                value: this.getPropertyValueTranslation(property.key, {
                  year: propertyValue.substring(0, 4),
                  month: propertyValue.substring(4, 6)
                })
              })
            }
          } else if (property.key === 'category') {
            if (propertyValue) {
              // TODO: use getPropertyLabelTranslation/getPropertyValueTranslation with a i18n namespace option
              data.push({
                label: this.$t('vehicleClass.' + vehicleClass + '.category'),
                value: this.$t(
                  'vehicleClass.' + vehicleClass + '.category' + propertyValue
                )
              })
            }
          } else {
            if (typeof property.value === 'string') {
              // edge case: if property key + value is set, the value will be used as label, and the value will be a boolean
              data.push({
                label: this.getPropertyLabelTranslation(
                  property.key,
                  property.value
                ),
                value: this.getPropertyValueTranslation(
                  property.key,
                  (Array.isArray(propertyValue)
                    ? propertyValue
                    : [propertyValue]
                  ).includes(property.value)
                )
              })
            } else {
              // determine translation based on property data value
              data.push({
                label: this.getPropertyLabelTranslation(property.key),
                value: this.getPropertyValueTranslation(
                  property.key,
                  propertyValue
                )
              })
            }
          }
        }
      })

      return data
    },
    getPropertyLabelTranslation(property, propertyValue) {
      return this.$t(
        'vehicle.' +
          property +
          (typeof propertyValue === 'string' ? propertyValue : '')
      )
    },
    getPropertyValueTranslation(property, arg2, arg3) {
      const isObject = v => typeof v === 'object' && !Array.isArray(v)
      let translation
      const propertyValue = isObject(arg2) ? undefined : arg2
      const replacements = isObject(arg2) ? arg2 : arg3

      if (typeof replacements === 'object') {
        translation = this.$t('vehicle.' + property + 'TEXT', replacements)
      } else if (typeof propertyValue === 'boolean') {
        translation = this.$t(
          'vdp.detailInformation.properties.boolean.' +
            (propertyValue ? 'yes' : 'no')
        )
      } else if (typeof propertyValue === 'number') {
        translation = propertyValue
      } else if (Array.isArray(propertyValue)) {
        translation = propertyValue
          .map(val => this.$t('vehicle.' + property + val))
          .join(', ')
      } else if (typeof propertyValue === 'string') {
        translation = this.$t('vehicle.' + property + propertyValue)
      } else {
        translation = propertyValue
      }

      return translation
    }
  }
}
</script>
